import styled from "styled-components";
import { UserStatus } from "@shared_packages/toolkit/types";
import { AppRoutes } from "../../../../constants/routes";
import type {
  CompanyMembershipFullApiType,
  RoleApiType,
  UserWithCompanyMemberships,
} from "../../../../util/types";
import {
  buildRelatedCompanyFieldGroup,
  type DetailCardConfig,
} from "../../components/detail-cards";

const RoleLink = styled.span`
  color: #027aff;
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: #0056cc;
  }
`;

interface BuildUserPersonalDetailsCardConfigArgs {
  canManageUsers: boolean;
  onEditClick: () => void;
}

export function buildUserPersonalDetailsCardConfig(
  args: BuildUserPersonalDetailsCardConfigArgs
): DetailCardConfig<UserWithCompanyMemberships> {
  return {
    title: "Personal details",
    editable: args.canManageUsers,
    onEdit: args.canManageUsers ? args.onEditClick : undefined,
    fields: [
      { label: "Fena ID", displayType: "text", accessorKey: "_id" },
      { label: "First name", displayType: "text", accessorKey: "firstName" },
      { label: "Last name", displayType: "text", accessorKey: "lastName" },
      { label: "Email", displayType: "email", accessorKey: "email" },
      {
        label: "Email Verified",
        displayType: "boolean",
        accessorKey: "emailVerified",
      },
      {
        label: "Phone Number",
        displayType: "text",
        getValue: (user) =>
          user.phoneNumber
            ? [user.phoneCode, user.phoneNumber].filter(Boolean).join(" ")
            : undefined,
      },
      {
        label: "Phone Number Verified",
        displayType: "boolean",
        accessorKey: "phoneVerified",
      },
      { label: "MFA Method", displayType: "text", accessorKey: "mfaMethod" },
      {
        label: "Last Activity",
        displayType: "date",
        accessorKey: "lastActivity",
      },
      {
        label: "Exists in Firebase",
        displayType: "boolean",
        getValue: (data) => Boolean(data.identityProviderReference),
      },
    ],
  };
}

interface BuildUserMembershipCardConfigArgs {
  canManageUsers: boolean;
  onActivateClick: (companyId: string) => Promise<void>;
  onBanClick: (companyId: string) => Promise<void>;
  onRoleClick: (role: RoleApiType) => void;
}

export function buildUserMembershipCardConfig(
  index: number,
  args: BuildUserMembershipCardConfigArgs
): DetailCardConfig<CompanyMembershipFullApiType> {
  return {
    title: `Company Membership #${index + 1}`,
    fields: [
      ...buildRelatedCompanyFieldGroup<CompanyMembershipFullApiType>(
        "company",
        { companyDetailsRoute: AppRoutes.Private.Companies.DETAILS }
      ),
      {
        label: "Role",
        displayType: "custom",
        render: (data) => (
          <RoleLink onClick={() => args.onRoleClick(data.role)}>
            {data.role?.name} {data.role?.isDefaultRole ? "(Default)" : ""}
          </RoleLink>
        ),
      },
      {
        label: "Status in company",
        displayType: "status",
        accessorKey: "status",
        actionsHidden: !args.canManageUsers,
        actions: [
          {
            label: "Activate",
            onClick: (data) =>
              args.onActivateClick(String(data.company?._id ?? "")),
            disabled: (data) => data.status === UserStatus.ACTIVE,
            confirmMessage:
              "Are you sure you want to activate this membership?",
          },
          {
            label: "Ban",
            onClick: (data) => args.onBanClick(String(data.company?._id ?? "")),
            disabled: (data) => data.status === UserStatus.BANNED,
            confirmMessage: "Are you sure you want to ban this membership?",
          },
        ],
      },
      {
        label: "Added to company",
        displayType: "date",
        accessorKey: "createdAt",
      },
    ],
  };
}
